/**
 * 陣地帯（ホームゾーン）の純粋ジオメトリ計算 — three.js / DOM 非依存
 * （node でテスト可能）。
 *
 * 盤の両端（row 0 の外側 / row size-1 の外側）に置く「陣地帯」と、
 * 手番プレイヤーのゴール側の帯に載せるシェブロン（進行方向マーク）の
 * 位置・サイズを boardSize から導出する。描画（board.js）はこの値を
 * そのまま three.js のジオメトリへ流し込むだけにする。
 *
 * 座標系: worldFromCell と同じく row → z 軸。row 0 側が -z、row size-1 側が +z。
 * 先手（player 0）の陣地は row 0 側（青）、ゴールは row size-1（後手の赤い帯）。
 * 後手（player 1）はその逆。「相手の陣地の帯に到達すれば勝ち」。
 */

export const ZONE = {
  depth: 0.8,    // 帯の奥行き（z方向）
  margin: 0.1,   // タイル外周と帯の隙間
  height: 0.06,  // 帯の厚み（薄いプレート）
  baseRim: 0.4,  // 盤土台の左右（x方向）の張り出し（既存値 0.8/2）
  basePad: 0.15, // 帯のさらに外側に残す土台の縁
  pulsePeriod: 2000,   // ゴール帯の明滅周期 (ms)
  emissiveBase: 0.25,  // 常時表示の帯の発光強度
  emissiveLo: 0.25,    // 明滅の下限
  emissiveHi: 0.85,    // 明滅の上限
  emissiveStatic: 0.6, // reduced-motion 時: 明滅せず静的に少し明るく
};

/** タイルの陣地 tint（半面ほぼ均一）。境目で色がパキッと切り替わる。 */
export const TINT = {
  min: 0.16, // 半面の中央寄りの行
  max: 0.22, // ホーム行（自陣の奥）
};

/** 陣地境界ライン（中央行を挟む溝の底に敷く細い静的発光帯）。 */
export const BOUNDARY = {
  widthRatio: 0.6,        // ラインの太さ = 溝幅 × この比率
  height: 0.015,          // 平たく低い帯（壁 0.85 と見間違えない形状）
  opacity: 0.35,
  emissiveIntensity: 0.45,
};

/** 盤（タイル+溝）の一辺の長さ。 */
export function boardSpan(size, { tile = 1.0, gap = 0.3 } = {}) {
  return size * tile + (size - 1) * gap;
}

/**
 * row のタイルに載せる陣地 tint。{ player: 0 | 1 | null, amount }。
 * player 0 = row 0 側（-z / 青）、player 1 = row size-1 側（+z / 赤）。
 * 奇数サイズの中央行だけ無色（player: null, amount: 0）。
 * 半面内は min（中央寄り）→ max（ホーム行）のごく緩い勾配にとどめ、
 * 「中央の境目で色が切り替わる」ことを読み取りやすくする。
 * 偶数サイズは中央行が無いので全行に tint が付く（境目は盤中心の溝）。
 */
export function territoryTint(row, size, opts = {}) {
  const { min = TINT.min, max = TINT.max } = opts;
  const mid = (size - 1) / 2;
  if (row === mid) return { player: null, amount: 0 };
  const player = row < mid ? 0 : 1;
  const d = Math.abs(row - mid);       // 中央からの距離（ホーム行で mid）
  const dMin = Number.isInteger(mid) ? 1 : 0.5; // 中央に最も近い行の距離
  const k = mid > dMin ? (d - dMin) / (mid - dMin) : 1;
  return { player, amount: min + (max - min) * k };
}

/**
 * 陣地境界ラインのスペック [{ player, x, z, width, depth }]。
 * 奇数サイズ: 中央行を挟む2本の溝（±pitch/2）の中心に、盤幅いっぱいの
 * 細い帯を1本ずつ（row 0 側 = player 0 / 青、row size-1 側 = player 1 / 赤）。
 * 偶数サイズ: 中央の溝1本（z=0）を -z 側 = 青 / +z 側 = 赤で半分ずつ
 * 分け合い、盤中心で色が切り替わる（グラデーション相当を純ジオメトリで）。
 * レイキャスト対象には入れない前提の見た目専用スペック。
 */
export function boundaryLineSpecs(size, opts = {}) {
  const { tile = 1.0, gap = 0.3, widthRatio = BOUNDARY.widthRatio } = opts;
  const pitch = tile + gap;
  const span = boardSpan(size, opts);
  const depth = gap * widthRatio;
  if (size % 2 === 1) {
    return [
      { player: 0, x: 0, z: -pitch / 2, width: span, depth },
      { player: 1, x: 0, z: pitch / 2, width: span, depth },
    ];
  }
  return [
    { player: 0, x: 0, z: -depth / 4, width: span, depth: depth / 2 },
    { player: 1, x: 0, z: depth / 4, width: span, depth: depth / 2 },
  ];
}

/**
 * 陣地帯2枚のスペック。[0] = 先手の陣地（row 0 側 / -z / 青）、
 * [1] = 後手の陣地（row size-1 側 / +z / 赤）。
 * 帯はタイル外周から margin だけ離れた盤土台の上に載る。
 */
export function zoneBandSpecs(size, opts = {}) {
  const { depth = ZONE.depth, margin = ZONE.margin } = opts;
  const span = boardSpan(size, opts);
  const zc = span / 2 + margin + depth / 2;
  return [
    { player: 0, width: span, depth, x: 0, z: -zc },
    { player: 1, width: span, depth, x: 0, z: zc },
  ];
}

/**
 * 盤土台の XZ サイズ。z 方向は陣地帯全体が土台の上に載るよう
 * （帯の外縁 + basePad まで）広げる。
 */
export function baseSpan(size, opts = {}) {
  const {
    depth = ZONE.depth,
    margin = ZONE.margin,
    rim = ZONE.baseRim,
    pad = ZONE.basePad,
  } = opts;
  const span = boardSpan(size, opts);
  return {
    x: span + rim * 2,
    z: span + (margin + depth + pad) * 2,
  };
}

/**
 * 山形（∧）マークの2Dポリゴン頂点列 [[x, y], ...]。+y 方向を指す。
 * 描画側は rotateX(π/2) で xz 平面に寝かせる（+y → +z）。
 */
export function chevronOutline({ width = 0.44, depth = 0.2, thickness = 0.1 } = {}) {
  const hw = width / 2;
  const apex = depth / 2;
  const base = -depth / 2;
  return [
    [-hw, base - thickness],
    [-hw, base],
    [0, apex],
    [hw, base],
    [hw, base - thickness],
    [0, apex - thickness],
  ];
}

/**
 * player のゴール側の帯（= 相手の陣地帯）の中央付近に並べるシェブロンの
 * 配置スペック [{ x, z, rotY }]。進行方向（先手 +z / 後手 -z）を指す。
 * rotY = 0 が +z 向き、π が -z 向き（帯の中心に対して対称に並ぶ）。
 */
export function chevronSpecs(size, player, opts = {}) {
  const { count = 3, spacing = 0.2, ...bandOpts } = opts;
  const band = zoneBandSpecs(size, bandOpts)[player === 0 ? 1 : 0];
  const rotY = player === 0 ? 0 : Math.PI;
  const specs = [];
  for (let i = 0; i < count; i++) {
    specs.push({ x: 0, z: band.z + (i - (count - 1) / 2) * spacing, rotY });
  }
  return specs;
}

/**
 * ゴール帯の明滅カーブ。t=0 で lo から始まり、period/2 で hi、period で lo に
 * 戻るゆるやかな sin 波（cos 形で位相を lo 始まりにしている）。
 */
export function goalPulse(tMs, opts = {}) {
  const {
    period = ZONE.pulsePeriod,
    lo = ZONE.emissiveLo,
    hi = ZONE.emissiveHi,
  } = opts;
  const k = 0.5 - 0.5 * Math.cos(((tMs % period) / period) * Math.PI * 2);
  return lo + (hi - lo) * k;
}
